import { isWalkable } from './path';
import type { Grid } from './path';

/*
 * פיזור יעדים לקבוצה.
 *
 * בלי זה, פקודת תנועה לקבוצה שולחת את כולם לאותו אריח — הם נערמים זה על זה
 * ונראים כמו יחידה אחת. פיזור ליעדים סמוכים הוא גם הדבר שמונע את רוב מצבי
 * ההידחקות עוד לפני שיש בכלל התחמקות מקומית.
 *
 * הסדר קבוע לחלוטין: ספירלה מהיעד החוצה, והיחידות מקבלות אריחים לפי סדר המזהים.
 * שני מחשבים שמריצים את אותה פקודה יקבלו בדיוק את אותו שיבוץ.
 */

/**
 * מרווח בין יחידה ליחידה, באריחים.
 *
 * היה 1 — כלומר יחידות סמוכות אריח-באריח. אריח הוא 256 יחידות עולם ורדיוס טנק
 * הוא 110, כך שהן לא ממש חפפו, אבל הן נגעו: הקבוצה נראתה כמו גוש אחד, כל תנועה
 * נראתה כמו התנגשות, וכמעט אי אפשר היה לכוון את האצבע ליחידה בודדת.
 *
 * 2 נותן פער של אריח שלם ביניהן — הקבוצה נקראת כקבוצה של יחידות נפרדות, ויש
 * לאן להקיש. **זה גם מה שמונע את רוב מצבי ההידחקות** עוד לפני שיש התחמקות מקומית.
 */
const SPACING = 2;

/** עד כמה טבעות מתרחקים מהיעד לפני שמוותרים ומשבצים את היעד עצמו. */
const MAX_RING = 5;

export function assignFormation(
    grid: Grid,
    goalX: number,
    goalY: number,
    count: number,
): number[] {
    const goal = goalY * grid.w + goalX;
    if (count <= 1) return [goal];

    const tiles: number[] = [];
    const taken = new Set<number>();

    /*
     * המקומות נבחרים על סריג בצעדי SPACING ולא על אריחים סמוכים. הסריג הוא מה
     * שקובע את המרווח — לא ההגרלה ולא ההתנגשות בזמן אמת.
     */
    const offer = (cellX: number, cellY: number) => {
        if (tiles.length >= count) return;
        const x = goalX + cellX * SPACING;
        const y = goalY + cellY * SPACING;
        if (!isWalkable(grid, x, y)) return;
        const index = y * grid.w + x;
        if (taken.has(index)) return;
        taken.add(index);
        tiles.push(index);
    };

    offer(0, 0);

    for (let ring = 1; ring <= MAX_RING && tiles.length < count; ring++) {
        /*
         * **הטבעת נמסרת בזוגות מנוגדים, לא ברצף סביבה.**
         *
         * מסלול רגיל מתחיל בפינה, ולכן קבוצה קטנה מתמלאת כולה לצד אחד של היעד.
         * זה לא רק נראה עקום — זו הטיה כיוונית: `simBatch` הראה שכוח שהתצורה שלו
         * נופלת בין הבסיסים מקבל יתרון על כוח שהתצורה שלו נופלת מעבר לבסיס האויב,
         * למרות שהמפה סימטרית לחלוטין.
         *
         * מסירה בזוגות (‎+d ומיד ‎−d) הופכת **כל קידומת** של הרשימה לסימטרית סביב
         * היעד, בכל גודל קבוצה. הכוח מתפרס סביב הנקודה שאליה נשלח, לא לצידה.
         */
        const cycle = ringCycle(ring);
        const half = cycle.length / 2;
        for (let i = 0; i < half && tiles.length < count; i++) {
            const near = cycle[i];
            const far = cycle[i + half];
            if (near) offer(near[0], near[1]);
            if (far && tiles.length < count) offer(far[0], far[1]);
        }
    }

    // אם השטח צפוף מדי, מי שנשאר מקבל את היעד עצמו — עדיף מלא לזוז בכלל.
    while (tiles.length < count) tiles.push(goal);

    return tiles;
}

/**
 * הטבעת כמסלול סגור עם כיוון קבוע, מהפינה השמאלית-עליונה ועם כיוון השעון.
 * האורך הוא 8·ring, ולכן האיבר הנגדי לאיבר i נמצא תמיד ב-i+4·ring — וזו התכונה
 * שמאפשרת למסור זוגות מנוגדים בלי לחשב היפוך בכל צעד.
 */
function ringCycle(ring: number): readonly (readonly [number, number])[] {
    const cycle: [number, number][] = [];
    for (let x = -ring; x < ring; x++) cycle.push([x, -ring]);
    for (let y = -ring; y < ring; y++) cycle.push([ring, y]);
    for (let x = ring; x > -ring; x--) cycle.push([x, ring]);
    for (let y = ring; y > -ring; y--) cycle.push([-ring, y]);
    return cycle;
}
